import type { FC } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { padNumberToDisplay, parseIntOrNull } from 'app/utils/numbers';
import InputNumber from 'app/components/Input/InputNumber';
import Icon from 'app/components/Icon';
import Chrono from 'app/icons/chrono.svg';

import style from './style.scss';

interface Props {
  value: number | null;
  onChange: (value: number | null) => void;
  title?: string;
  exampleDuration?: string;
}

const InputTime: FC<Props> = ({ value, onChange, title, exampleDuration }) => {
  const [t] = useTranslation();

  const timerSecondsValue = value ? value % 60 : null;
  const timerMinutesValue = value ? Math.floor(value / 60) : null;

  const handleChange = (
    newTimerMinutesValue: number | null,
    newTimerSecondsValue: number | null
  ) => {
    onChange((newTimerMinutesValue || 0) * 60 + (newTimerSecondsValue || 0));
  };

  return (
    <div>
      {title && <div className={style.title}>{title}</div>}
      <div className={style.timer}>
        <Icon href={Chrono} className={style.icon} />
        <InputNumber
          value={timerMinutesValue !== null ? padNumberToDisplay(timerMinutesValue) : ''}
          onChange={value => {
            const minutesValue = parseIntOrNull(value);
            return handleChange(
              minutesValue !== null ? Math.min(999, minutesValue) : minutesValue,
              timerSecondsValue
            );
          }}
          incrementBy={1}
          className={classNames(style.input, style.minutes)}
        />
        :
        <InputNumber
          value={timerSecondsValue !== null ? padNumberToDisplay(timerSecondsValue) : ''}
          onChange={value => {
            const secondsValue = parseIntOrNull(value);
            return handleChange(
              timerMinutesValue,
              secondsValue !== null ? Math.min(59, secondsValue) : secondsValue
            );
          }}
          incrementBy={1}
          className={classNames(style.input, style.seconds)}
        />
      </div>
      {exampleDuration && (
        <div className={style.example}>
          {t('component-timer_duration_dropdown-example')} {exampleDuration}
        </div>
      )}
    </div>
  );
};

InputTime.displayName = 'InputTime';

export default InputTime;
